import classnames from "classnames/bind";
import { Slot } from "../Slot/Slot";
import { LayoutProps } from "../layoutMap";
import styles from "./Default.module.scss";

const cx = classnames.bind(styles);

export const slotNameMap = {
  HEADER: "header",
  BANNER: "banner",
  TOP: "top",
  DETAILS_MEDIA_IMG: "details-media-img",
  DETAILS_MEDIA_BODY: "details-media-body",
  DETAILS_HEAD: "details-head",
  DETAILS_BODY: "details-body",
  ACCESSORY: "accessory",
  ACCESSORY_FOOT: "accessory-foot",
  MIDDLE: "middle",
  BOTTOM: "bottom",
  FOOTER: "footer",
} as const;

const IMAGE_SIZES = {
  FULL_WIDTH:
    "(min-width: 1248px) 1200px, (min-width: 768px) calc(100vw - 48px), (min-width: 568px) calc(100vw - 32px), 100vw",
  DETAILS:
    "(min-width: 1248px) 400px, (min-width: 1024px) calc((100vw - 48px) * 0.5 - 80px), (min-width: 768px) calc((100vw - 48px) * 0.5 - 48px), (min-width: 568px) calc(100vw - 64px), calc(100vw - 32px)",
  ACCESSORY:
    "(min-width: 1248px) 592px, (min-width: 1024px) calc((100vw - 48px) * 0.5), (min-width: 768px) calc((100vw - 48px) * 0.5), (min-width: 568px) calc(100vw - 32px), 100vw",
} as const;

export function Default(props: LayoutProps) {
  const layoutTestId = props.pageConfig?.layoutProps?.testId ?? "layout";
  const moveMobileGalleryOnTop =
    props.flags.moveWideMobileGalleryOnTop.value === true &&
    props.pageConfig?.layoutProps?.skipMobileGalleryTopReorder !== true;
  const typographyVariant =
    props.flags?.useNewTypography?.value === true
      ? "body-m-to-body-s"
      : "control";

  return (
    <div
      className={styles.page}
      data-layout="Default"
      data-typography-variant={typographyVariant}
    >
      <Slot {...props} name={slotNameMap["HEADER"]} sizes="100vw" />
      <main data-testid={layoutTestId} className={styles.main} id="main">
        {/* The banner needs its own grid container, otherwise an empty banner slot will insert an extra gap */}
        <div className={styles.gridContainer}>
          <Slot
            {...props}
            name={slotNameMap["BANNER"]}
            className={styles.banner}
            sizes={IMAGE_SIZES.FULL_WIDTH}
          />
        </div>
        <div className={cx("gridContainer", "noGap")}>
          <Slot
            {...props}
            name={slotNameMap["TOP"]}
            className={styles.top}
            sizes={IMAGE_SIZES.FULL_WIDTH}
          />
          <div
            data-testid="column-container"
            className={cx(styles.gridContainer, styles.twoColumnGrid)}
          >
            <div className={cx("gridCol")}>
              <div className={styles.detailsMediaWrapper}>
                <Slot
                  name={slotNameMap["DETAILS_MEDIA_IMG"]}
                  className={styles.detailsMediaImg}
                  {...props}
                  sizes={IMAGE_SIZES.DETAILS}
                />
                <Slot
                  name={slotNameMap["DETAILS_MEDIA_BODY"]}
                  className={styles.detailsMediaBody}
                  {...props}
                  sizes={IMAGE_SIZES.DETAILS}
                />
              </div>
              <Slot
                {...props}
                name={slotNameMap["DETAILS_HEAD"]}
                className={styles.detailsHead}
                sizes={IMAGE_SIZES.DETAILS}
              />
              <Slot
                {...props}
                name={slotNameMap["DETAILS_BODY"]}
                className={styles.detailsMain}
                sizes={IMAGE_SIZES.DETAILS}
              />
            </div>
            <div className={cx("gridCol")}>
              <Slot
                {...props}
                name={slotNameMap["ACCESSORY"]}
                className={cx("accessory", {
                  mobileGalleryOnTop: moveMobileGalleryOnTop,
                })}
                sizes={IMAGE_SIZES.ACCESSORY}
              />
              <Slot
                {...props}
                name={slotNameMap["ACCESSORY_FOOT"]}
                className={styles.accessoryFoot}
                sizes={IMAGE_SIZES.ACCESSORY}
              />
            </div>
          </div>
          <Slot
            {...props}
            name={slotNameMap["MIDDLE"]}
            className={styles.middle}
            sizes={IMAGE_SIZES.FULL_WIDTH}
          />
          <Slot
            {...props}
            name={slotNameMap["BOTTOM"]}
            className={styles.bottom}
            sizes={IMAGE_SIZES.FULL_WIDTH}
          />
        </div>
      </main>
      <Slot {...props} name={slotNameMap["FOOTER"]} sizes="100vw" />
    </div>
  );
}
